/**
 * Per-Safe SRC-20 token list — spec §9.
 *
 * Discovery flow:
 * 1. Render seed tokens immediately for first-paint.
 * 2. Fetch the explorer list and merge: explorer > seed (de-duped by address).
 * 3. Cache the merged list in module memory for 60s. Invalidated on
 *    `refetch()` and on chain/safe switch (different cache key).
 *
 * Manual token addition was removed per product decision — the explorer is
 * the sole source of truth.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { Address } from 'viem'
import useSafeAddress from '@/hooks/useSafeAddress'
import { logError, Errors } from '@/services/exceptions'
import { useSrc20Config } from './useSrc20Config'
import { fetchExplorerTokenList } from '../services/tokenList'
import { EXPLORER_LIST_CACHE_TTL_MS } from '../constants'
import type { Src20TokenInfo } from '../types'

type CacheEntry = { tokens: Src20TokenInfo[]; fetchedAt: number; error?: string }
const moduleCache = new Map<string, CacheEntry>()
const cacheKey = (chainId: number, safe: string) => `${chainId}:${safe.toLowerCase()}`

const mergeTokens = (...lists: Src20TokenInfo[][]): Src20TokenInfo[] => {
  // Earlier entries take precedence — pass in (explorer, seed) for the spec §9 merge order.
  const seen = new Map<string, Src20TokenInfo>()
  for (const list of lists) {
    for (const t of list) {
      const k = t.address.toLowerCase()
      if (!seen.has(k)) seen.set(k, t)
    }
  }
  return [...seen.values()]
}

export interface UseSrc20TokenListResult {
  tokens: Src20TokenInfo[]
  loading: boolean
  error: string | undefined
  refetch: () => Promise<void>
}

export const useSrc20TokenList = ({ enabled = true }: { enabled?: boolean } = {}): UseSrc20TokenListResult => {
  const cfg = useSrc20Config()
  const safeAddress = useSafeAddress()

  const seed = useMemo<Src20TokenInfo[]>(
    () =>
      (cfg?.seedTokens ?? []).map((t) => ({
        address: t.address,
        symbol: t.symbol,
        name: t.name,
        decimals: t.decimals,
        iconUrl: t.iconUrl ?? cfg?.defaultTokenIconUrl,
        source: 'seed' as const,
      })),
    [cfg],
  )

  const [explorerTokens, setExplorerTokens] = useState<Src20TokenInfo[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | undefined>(undefined)
  const inflight = useRef<Promise<void> | undefined>(undefined)

  const doFetch = useCallback(
    async (force: boolean) => {
      if (!cfg || !safeAddress || !enabled) return
      const key = cacheKey(cfg.chainId, safeAddress)
      const cached = moduleCache.get(key)
      const now = Date.now()
      if (!force && cached && now - cached.fetchedAt < EXPLORER_LIST_CACHE_TTL_MS) {
        setExplorerTokens(cached.tokens)
        setError(cached.error)
        return
      }
      setLoading(true)
      try {
        const tokens = await fetchExplorerTokenList(cfg, safeAddress as Address)
        moduleCache.set(key, { tokens, fetchedAt: now })
        setExplorerTokens(tokens)
        setError(undefined)
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err)
        moduleCache.set(key, { tokens: cached?.tokens ?? [], fetchedAt: now, error: msg })
        logError(Errors._601, msg)
        setError(msg)
      } finally {
        setLoading(false)
      }
    },
    [cfg, safeAddress, enabled],
  )

  useEffect(() => {
    setExplorerTokens([])
    setError(undefined)
    if (!cfg || !safeAddress || !enabled) return
    const key = cacheKey(cfg.chainId, safeAddress)
    const cached = moduleCache.get(key)
    if (cached && Date.now() - cached.fetchedAt < EXPLORER_LIST_CACHE_TTL_MS) {
      setExplorerTokens(cached.tokens)
      setError(cached.error)
      return
    }
    if (!inflight.current) {
      inflight.current = doFetch(false).finally(() => {
        inflight.current = undefined
      })
    }
  }, [cfg, safeAddress, enabled, doFetch])

  const tokens = useMemo(() => mergeTokens(explorerTokens, seed), [explorerTokens, seed])

  const refetch = useCallback(() => doFetch(true), [doFetch])

  return { tokens, loading, error, refetch }
}

export default useSrc20TokenList
